/**
 * Modal scanner code-barres EAN-13 / EAN-8 / Code128 via @zxing/browser.
 * Utilise la caméra arrière du téléphone/tablette.
 *
 * Props :
 * - onDetect(code: string) : callback déclenché quand un code-barres est lu
 * - onClose()              : ferme la modale
 */
import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { X, Camera, AlertCircle } from 'lucide-react';
import { Button } from '../../ui/button';

const BarcodeScannerDialog = ({ onDetect, onClose }) => {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const [error, setError] = useState(null);
  const [lastCode, setLastCode] = useState(null);

  useEffect(() => {
    const reader = new BrowserMultiFormatReader();
    let cancelled = false;

    (async () => {
      try {
        const devices = await BrowserMultiFormatReader.listVideoInputDevices();
        // Préférer la caméra arrière (label contient "back" ou "rear" ou "environment")
        const rear = devices.find(d => /back|rear|environment/i.test(d.label));
        const deviceId = rear?.deviceId || devices[0]?.deviceId;
        if (!deviceId) {
          setError("Aucune caméra détectée. Vérifiez les permissions navigateur.");
          return;
        }
        const controls = await reader.decodeFromVideoDevice(
          deviceId, videoRef.current,
          (result, err) => {
            if (cancelled) return;
            if (result) {
              const text = result.getText();
              setLastCode(text);
              // Petit délai pour que l'UI flash, puis on remonte au parent
              setTimeout(() => {
                if (!cancelled) onDetect(text);
              }, 250);
            }
            // err est jeté sur chaque frame sans code → ignorer (ce n'est pas une erreur fatale)
          }
        );
        controlsRef.current = controls;
      } catch (e) {
        setError(e?.message || "Impossible d'accéder à la caméra");
      }
    })();

    return () => {
      cancelled = true;
      try { controlsRef.current?.stop?.(); } catch (_e) { /* noop */ }
    };
  }, [onDetect]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 pb-[calc(env(safe-area-inset-bottom)+80px)] sm:pb-3"
      onClick={onClose}
      data-testid="barcode-scanner-dialog"
    >
      <div
        className="bg-white rounded-xl max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 bg-gradient-to-r from-[#5B3B8C] to-[#7C4FB8] px-5 py-3 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            <h2 className="font-semibold">Scanner code-barres</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="barcode-scanner-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="bg-black aspect-[4/3] relative">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              autoPlay
              playsInline
              muted
              data-testid="barcode-scanner-video"
            />
            {/* Reticle */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-3/4 h-1/3 border-2 border-purple-300 rounded-md shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
            </div>
            {lastCode && (
              <div className="absolute top-2 left-2 right-2 bg-emerald-500/90 text-white text-sm font-mono px-3 py-1.5 rounded-md text-center">
                ✓ {lastCode}
              </div>
            )}
          </div>

          <div className="p-4 space-y-2">
            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <p className="text-xs text-zinc-500 text-center">
              Cadrez le code-barres dans le rectangle. La détection est automatique (EAN-13, EAN-8, Code128).
            </p>
          </div>
        </div>

        <div className="shrink-0 p-3 border-t border-zinc-200 bg-white">
          <Button onClick={onClose} variant="outline" className="w-full" data-testid="barcode-scanner-cancel">
            Annuler
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScannerDialog;
